import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SuiSpinner } from '@splashthat/sui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useLocalStorage } from '@rehooks/local-storage';

import Logger from 'observability/Logger';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';

import GoldcastEmbed from './GoldcastEmbed';
import GoldcastMessage from './GoldcastMessage';
import { useGoldcastPolling } from './useGoldcastPolling';

const LOG = new Logger('GoldcastEmbedContainer');

const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const GoldcastEmbedContainer = ({ config }) => {
    const { eventId: goldcastEventId, attendee, attendeeCreationRequired } = config ?? {};

    const { event } = useTouchpointData();
    const [vepTokens] = useLocalStorage(`splashVirtualEvent/${event?.id}`);
    const guestAccessHash = vepTokens?.guestAccessHash;

    const hasAttendeeConfig = Boolean(goldcastEventId && attendee);
    const canPoll = Boolean(event?.id && guestAccessHash && goldcastEventId);
    const shouldPoll = Boolean(!hasAttendeeConfig && attendeeCreationRequired && canPoll);

    React.useEffect(() => {
        if (!hasAttendeeConfig && !attendeeCreationRequired) {
            LOG.error('Attendee config missing and creation not required: possible misconfiguration', {
                goldcastEventId,
            });
        }
        if (attendeeCreationRequired && !canPoll) {
            LOG.error('Goldcast attendee polling skipped: missing required params', {
                splashEventId: event?.id,
                goldcastEventId,
                hasGuestAccessHash: Boolean(guestAccessHash),
            });
        }
    }, [hasAttendeeConfig, attendeeCreationRequired, canPoll, goldcastEventId, event?.id, guestAccessHash]);

    const { resolvedAttendee, isTimedOut, hasError } = useGoldcastPolling({
        enabled: shouldPoll,
        eventId: event?.id,
        guestAccessHash,
    });

    if (hasAttendeeConfig) {
        return <GoldcastEmbed config={config} />;
    }

    if (!attendeeCreationRequired || !canPoll) {
        return <GoldcastMessage message="Something went wrong. Please try again later." />;
    }

    if (resolvedAttendee) {
        return <GoldcastEmbed config={{ eventId: goldcastEventId, attendee: resolvedAttendee }} />;
    }

    if (isTimedOut || hasError) {
        return (
            <GoldcastMessage message="Your virtual experience is taking longer than expected. Please try again in a few minutes." />
        );
    }

    // Polling in progress
    return (
        <SpinnerWrapper>
            <SuiSpinner loading size="large" />
            <VisuallyHidden role="status">loading</VisuallyHidden>
        </SpinnerWrapper>
    );
};

GoldcastEmbedContainer.propTypes = {
    config: PropTypes.shape({
        eventId: PropTypes.string,
        attendee: PropTypes.shape({
            shortId: PropTypes.string,
            secretCode: PropTypes.string,
            emailId: PropTypes.string,
        }),
        attendeeCreationRequired: PropTypes.bool,
    }),
};

GoldcastEmbedContainer.defaultProps = {
    config: null,
};

export default GoldcastEmbedContainer;
